import { z } from 'zod';

/**
 * The draft and the queue of unsent messages (chat spec §8).
 *
 * **They live in the browser, and that is a requirement rather than a
 * convenience.** §8 is explicit: « le brouillon non envoyé survit au
 * rafraîchissement », and React state does not survive an `F5` - which is
 * exactly the case being covered. So the pair lives in `localStorage`, keyed by
 * room and by role, and the component reads it at mount instead of owning it.
 *
 * Why there is a QUEUE at all, in one line: the author chose a live composer
 * over a blocked one (§14), so the field stays usable through an outage and the
 * messages leave at the reconnection. That choice is what makes the idempotency
 * key of §8.1 compulsory - without it a flush after a lost acknowledgement
 * posts the same message twice - and the key is minted here, once per message,
 * and kept in the queue until the server has answered for it.
 *
 * Everything below the storage accessors is PURE: the queue's rules are worth a
 * test, and a test should not need a browser to run them.
 */

// ── The shapes ──────────────────────────────────────────────────────────────

/**
 * One message waiting to leave.
 *
 * It carries its OWN `composedAt`, taken when the person pressed the button and
 * never when the queue drains, because those two instants are the whole point
 * of §8.2: the transcript renders in the order of arrival and shows the two
 * hours when they diverge. A queue that stamped at the flush would erase the
 * only evidence that a message was written before the cut.
 */
export const pendingChatMessageSchema = z
  .object({
    clientMessageId: z.string().uuid(),
    text: z.string().min(1),
    composedAt: z.string().datetime(),
  })
  .strict();
export type PendingChatMessage = z.infer<typeof pendingChatMessageSchema>;

/** What one room, on one side, holds in the browser. */
export const chatOutboxSchema = z
  .object({
    /** What is typed and not sent. Empty string, never absent. */
    draft: z.string(),
    /** Minted, ordered by composition, and drained from the front. */
    pending: z.array(pendingChatMessageSchema),
  })
  .strict();
export type ChatOutbox = z.infer<typeof chatOutboxSchema>;

export const EMPTY_CHAT_OUTBOX: ChatOutbox = { draft: '', pending: [] };

// ── Where it is kept ────────────────────────────────────────────────────────

/**
 * The storage key: one per room AND per role.
 *
 * Per ROOM because two interviews open in two tabs must not pour into each
 * other. Per ROLE because both links can legitimately be open in the same
 * browser - that is how the whole thing is tested, and how the captures are
 * taken - and a shared key would post the interviewer's draft as the guest.
 *
 * Keyed on the room's REFERENCE and not on the token: the reference identifies
 * the room without being a credential, and a secret in `localStorage` under a
 * predictable key is one `document.cookie`-grade mistake away from being read
 * by anything else on the origin.
 */
export function chatOutboxStorageKey(reference: string, role: string): string {
  return `matter-extractor:chat-outbox:${reference}:${role}`;
}

/**
 * What this module needs of a store. `localStorage` satisfies it; so does a
 * `Map` in a test, which is why it is a parameter and not an import.
 */
export interface ChatOutboxStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Reads the outbox, and answers an EMPTY one for anything it cannot read.
 *
 * Parsed with Zod rather than cast, because `localStorage` is a trust boundary
 * like any other: what is in it was written by a version of this code that may
 * no longer exist, and a queue whose entries have lost a field would be flushed
 * as `undefined` into a POST. Total on purpose - a stored value nobody can read
 * costs a lost draft, and a throw here would cost the whole screen.
 *
 * It also means the private-window case is free: Safari throws on
 * `localStorage` access, and the person still gets a working conversation
 * without a persisted draft.
 */
export function readChatOutbox(storage: ChatOutboxStorage, key: string): ChatOutbox {
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return EMPTY_CHAT_OUTBOX;
  }
  if (raw === null) return EMPTY_CHAT_OUTBOX;
  try {
    const parsed = chatOutboxSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : EMPTY_CHAT_OUTBOX;
  } catch {
    return EMPTY_CHAT_OUTBOX;
  }
}

/**
 * Writes the outbox, and SWALLOWS a failure - the one place in this lot that
 * does, with a reason.
 *
 * A store that refuses (private mode, a full quota) must not take the
 * conversation down: the queue still works in memory for as long as the tab
 * lives, which is strictly better than a screen that throws while somebody is
 * mid-sentence. What is lost is the `F5` guarantee, on a browser that cannot
 * offer it anyway.
 */
export function writeChatOutbox(
  storage: ChatOutboxStorage,
  key: string,
  outbox: ChatOutbox,
): boolean {
  try {
    storage.setItem(key, JSON.stringify(outbox));
    return true;
  } catch {
    return false;
  }
}

// ── Its rules ───────────────────────────────────────────────────────────────

/** Replaces the draft, leaving the queue alone. */
export function withChatDraft(outbox: ChatOutbox, draft: string): ChatOutbox {
  return { ...outbox, draft };
}

/**
 * Moves the draft into the queue as one message, and clears it.
 *
 * The key is minted by the CALLER - `crypto.randomUUID()` in the component -
 * so this stays pure and a test can pin it. Returns the outbox unchanged when
 * there is nothing but whitespace: a queue entry with no text can never be
 * accepted, and a permanent refusal in the queue is the one thing that jams it.
 */
export function enqueueChatMessage(
  outbox: ChatOutbox,
  message: PendingChatMessage,
): { outbox: ChatOutbox; enqueued: PendingChatMessage | null } {
  const text = message.text.trim();
  if (text === '') return { outbox, enqueued: null };
  // A key already in the queue is a caller bug, not a duplicate to merge: it
  // would silently replace a message somebody wrote. Refused loudly by doing
  // nothing, the same shape as the empty case.
  if (outbox.pending.some((entry) => entry.clientMessageId === message.clientMessageId)) {
    return { outbox, enqueued: null };
  }
  const enqueued = { ...message, text };
  return { outbox: { draft: '', pending: [...outbox.pending, enqueued] }, enqueued };
}

/**
 * Drops one message from the queue, by its key.
 *
 * Used for BOTH outcomes that end a message's life in the queue: the server
 * stored it (201, `duplicate` either way - §8.1 makes a re-send a success), and
 * the server refused it permanently. The two are the same gesture here because
 * the difference is what the screen says, not what the queue does.
 */
export function dropChatMessage(outbox: ChatOutbox, clientMessageId: string): ChatOutbox {
  return {
    ...outbox,
    pending: outbox.pending.filter((entry) => entry.clientMessageId !== clientMessageId),
  };
}

/**
 * The next message to send, or null.
 *
 * The FRONT of the queue and one at a time, so the arrival order matches the
 * composition order whenever the network allows it. Sent in parallel, three
 * queued messages would race and land in whatever order the server happened to
 * take them - and §8.2 says the transcript is read in the order of arrival, so
 * the client owes the person the least surprising one it can get.
 */
export function nextChatMessage(outbox: ChatOutbox): PendingChatMessage | null {
  return outbox.pending[0] ?? null;
}
